import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';

/**
 * Shared layout for legal pages (Privacy Policy / Terms of Use).
 * Children should be the document content (sections).
 */
export const LegalLayout = ({ title, updated, children }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main className="bg-black text-white min-h-screen">
            <Navbar />

            <section className="relative pt-32 sm:pt-36 pb-20 border-b border-white/5">
                {/* Background grid */}
                <div className="absolute inset-0 hero-grid pointer-events-none" />

                <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#8e8e93] hover:text-[#b8ff00] transition-colors mb-8"
                    >
                        <ArrowLeft size={14} />
                        Back to home
                    </Link>

                    <div className="text-xs uppercase tracking-[0.25em] text-[#b8ff00] font-semibold mb-4">
                        — Legal
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.02] mb-5">
                        {title}
                    </h1>
                    <div className="text-sm text-[#8e8e93]">
                        <span className="text-white/80 font-medium">
                            HyperTroop Fitness Tracker
                        </span>
                        <span className="mx-2">·</span>
                        Last updated: {updated}
                    </div>
                </div>
            </section>

            <article className="relative py-16 sm:py-24">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 legal-content">
                    {children}
                </div>
            </article>

            <Footer />
        </main>
    );
};

/* ============ Small helpers for clean legal markup ============ */
export const Section = ({ n, title, children }) => (
    <section className="mb-12">
        <h2 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight mb-4 flex items-baseline gap-3">
            <span className="text-[#b8ff00] text-base font-mono">
                {String(n).padStart(2, '0')}
            </span>
            {title}
        </h2>
        <div className="text-[15px] leading-[1.75] text-[#c8c8cd] space-y-4">
            {children}
        </div>
    </section>
);

export const SubTitle = ({ children }) => (
    <h3 className="text-white font-semibold text-[15px] mt-5 mb-1">
        {children}
    </h3>
);

export const Bullets = ({ items }) => (
    <ul className="space-y-2 mt-2">
        {items.map((item, i) => (
            <li key={i} className="flex gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#b8ff00] flex-shrink-0" />
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

export const Strong = ({ children }) => (
    <strong className="text-white font-semibold">{children}</strong>
);

export const Mail = ({ children }) => (
    <a
        href={`mailto:${children}`}
        className="text-[#b8ff00] hover:underline font-medium"
    >
        {children}
    </a>
);

export default LegalLayout;
